import {
  ChangeEvent,
  UIEvent,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  NotificationOption,
  NotificationSetting,
} from 'features/notifications/notifications.types';
import { useNotifications } from 'features/notifications/hooks/useNotifications';
import { getSortedRegionsSections } from 'shared/utils';
import { useAuthState } from 'state/useAuthState';
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { debounce } from 'es-toolkit/compat';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { enqueueSnackbar } from 'notistack';
import { NotificationTypes } from 'features/notifications/notifications.constants';
import { MEMBERSHIP_PLANS } from 'features/membership/membership.constants';
import { getPaywallLink } from 'features/membership/membership.utils';
import { useSessionStorage } from 'usehooks-ts';
import { RegionList } from './RegionList';
import { RegionListItem } from './RegionListItem';
import { RegionSelectorProps } from './types';

const MAX_SUBSCRIBED_REGIONS_FOR_NON_MEMBERS = 4;
const REGION_SETTINGS_STORAGE_KEY = 'REGION_SETTINGS';
const EXPANDED_LISTS_STORAGE_KEY = 'EXPANDED_LISTS';

const useStyles = makeStyles()((theme) => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  lists: {
    overflowY: 'auto',
    flex: 1,
  },
  search: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  shadowTop: {
    boxShadow: '0px 4px 4px 0px #00000040',
  },
  shadowBottom: {
    boxShadow: '0px -4px 4px 0px #00000040',
  },
}));

const getNextNotificationKey = (
  isCurrentlyEnabled: boolean,
  previousSetting: NotificationOption | undefined,
): NotificationOption => {
  if (isCurrentlyEnabled) return NotificationTypes.OFF.key;
  if (previousSetting && previousSetting !== NotificationTypes.OFF.key) {
    return previousSetting;
  }
  return NotificationTypes.ALL.key;
};

const updateRegionSettings = (
  existingSettings: NotificationSetting[],
  newSetting: NotificationSetting,
): NotificationSetting[] =>
  existingSettings.map((regionSetting) => {
    if (regionSetting.region.id === newSetting.region.id) {
      return newSetting;
    }

    return regionSetting;
  });

export const RegionSelector = (props: RegionSelectorProps): JSX.Element => {
  const {
    regionSettings,
    onboarding,
    maxChecked = MAX_SUBSCRIBED_REGIONS_FOR_NON_MEMBERS,
  } = props;
  const { classes } = useStyles();
  const { putNotificationSettings } = useNotifications({ queryEnabled: false });
  const { mutate, isPending } = putNotificationSettings;
  /**
   * Prompt blocker changes the app's route before prompting the user,
   * if the user cancels navigation, the current page route is restored
   * which causes a re-render, and the page state is reset. We use the
   * session storage to avoid losing the state changes.
   * Similar issue: https://github.com/remix-run/react-router/issues/5405
   */
  const [regionsState, setRegionsState] = useSessionStorage<
    NotificationSetting[]
  >(REGION_SETTINGS_STORAGE_KEY, regionSettings);
  const [search, setSearch] = useState<string>('');
  const [scrollPosition, setScrollPosition] = useState({
    top: true,
    bottom: false,
  });
  const [isScrollable, setIsScrollable] = useState(false);
  const [expandedLists, setExpandedLists] = useSessionStorage<
    Record<string, boolean>
  >(EXPANDED_LISTS_STORAGE_KEY, {});
  const listsBoxRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { showMembershipFeatures } = useAuthState();
  const history = useHistory();
  const theme = useTheme();
  const deferredSearch = useDeferredValue(search);

  const regionSettingsMap = useMemo(
    () => new Map(regionSettings.map((r) => [r.region.id, r.setting])),
    [regionSettings],
  );

  const pendingChanges = regionsState.some(
    (r) => r.setting !== regionSettingsMap.get(r.region.id),
  );

  const resetLocalState = useCallback(() => {
    sessionStorage.removeItem(REGION_SETTINGS_STORAGE_KEY);
    sessionStorage.removeItem(EXPANDED_LISTS_STORAGE_KEY);
  }, []);

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: pendingChanges,
    onConfirm: resetLocalState,
  });

  const numChecked = regionsState.filter(
    (r) => r.setting !== NotificationTypes.OFF.key,
  ).length;
  // Ref instead of a dep: keeps handleToggleRegion's reference stable across
  // toggles so renderedRegionSections doesn't invalidate on every checkbox
  // click, avoiding a full re-render of all visible items.
  const numCheckedRef = useRef(numChecked);
  numCheckedRef.current = numChecked;

  const transitionDuration = theme.transitions.duration.standard;

  const checkScrollableTimerRef = useRef<NodeJS.Timeout>();
  const checkScrollable = useCallback((): void => {
    clearTimeout(checkScrollableTimerRef.current);
    // Fire after collapse has hidden content
    checkScrollableTimerRef.current = setTimeout(() => {
      const containerElement = listsBoxRef.current;
      const canScroll = containerElement
        ? containerElement.scrollHeight >
          containerElement.getBoundingClientRect().height
        : false;
      setIsScrollable(canScroll);
    }, transitionDuration);
  }, [transitionDuration]);

  const handleToggleCollapse = useCallback(
    (sectionTitle: string): void => {
      setExpandedLists((prev) => ({
        ...prev,
        [sectionTitle]: !prev[sectionTitle],
      }));
      checkScrollable();
    },
    [setExpandedLists, checkScrollable],
  );

  useEffect(() => {
    checkScrollable();
    return () => {
      clearTimeout(checkScrollableTimerRef.current);
    };
  }, [checkScrollable]);

  const debouncedSetScrollPosition = useMemo(
    () => debounce(setScrollPosition, transitionDuration, { leading: true }),
    [transitionDuration],
  );

  useEffect(() => {
    return () => {
      debouncedSetScrollPosition.cancel();
    };
  }, [debouncedSetScrollPosition]);

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    if (!event.target) return;
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as HTMLDivElement;
    debouncedSetScrollPosition({
      top: scrollTop === 0,
      bottom: scrollTop + clientHeight >= scrollHeight,
    });
  };

  const handleToggleRegion = useCallback(
    (regionSetting: NotificationSetting): void => {
      const isCurrentlyEnabled =
        regionSetting.setting !== NotificationTypes.OFF.key;

      const previousSetting = regionSettingsMap.get(regionSetting.region.id);

      const key = getNextNotificationKey(isCurrentlyEnabled, previousSetting);

      // Currently selected items plus the one we want to add
      const updatedNumCheck = numCheckedRef.current + 1;
      if (
        !showMembershipFeatures &&
        key !== NotificationTypes.OFF.key &&
        updatedNumCheck > maxChecked
      ) {
        if (onboarding) {
          enqueueSnackbar(
            t('notifications.regionLimitReached', {
              maxCounties: maxChecked,
            }),
            { variant: 'error' },
          );
          return;
        }
        const paywallLink = getPaywallLink({
          focusedPlan: MEMBERSHIP_PLANS.yearly,
          source: 'select_counties_limit',
        });
        history.push(paywallLink.url, {
          fromRoute: history.location.pathname + history.location.search,
        });
        return;
      }

      const newRegionSetting: NotificationSetting = {
        ...regionSetting,
        setting: key,
      };

      setRegionsState((prev) => updateRegionSettings(prev, newRegionSetting));
    },
    [
      history,
      maxChecked,
      onboarding,
      regionSettingsMap,
      setRegionsState,
      showMembershipFeatures,
      t,
    ],
  );

  const regionSettingsSections = useMemo(
    () => getSortedRegionsSections(regionsState, deferredSearch),
    [regionsState, deferredSearch],
  );

  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  }, []);

  const isSearchSettled = search === deferredSearch;
  const hasNoResults =
    isSearchSettled && search !== '' && regionSettingsSections.length === 0;

  const renderedRegionSections = useMemo(() => {
    if (hasNoResults) return [];

    return regionSettingsSections.map((regionSection) => (
      <RegionList
        key={regionSection.title}
        name={regionSection.title}
        onToggleCollapse={() => handleToggleCollapse(regionSection.title)}
        expanded={Boolean(deferredSearch) || expandedLists[regionSection.title]}
      >
        {regionSection.regionSettings.map((regionSetting) => (
          <RegionListItem
            key={regionSetting.region.id}
            onChange={handleToggleRegion}
            regionSetting={regionSetting}
          />
        ))}
      </RegionList>
    ));
  }, [
    deferredSearch,
    expandedLists,
    handleToggleCollapse,
    handleToggleRegion,
    hasNoResults,
    regionSettingsSections,
  ]);

  const handleSave = (): void => {
    if (isPending) return;
    const updatedSettings = regionsState.filter(
      (r) => regionSettingsMap.get(r.region.id) !== r.setting,
    );

    mutate(updatedSettings, {
      onSuccess: () => {
        resetLocalState();
        unblockedNavigate(onboarding ? -1 : '/notifications');
      },
    });
  };

  return (
    <Box
      className={classes.container}
      sx={{ pointerEvents: isPending ? 'none' : 'auto' }}
    >
      <Box
        className={
          isScrollable && !scrollPosition.top ? classes.shadowTop : undefined
        }
      >
        <TextField
          placeholder={t('notificationSettingsRegionAdd.search')}
          fullWidth
          className={classes.search}
          onChange={handleSearch}
          sx={{ marginBottom: 3 }}
          value={search}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearch('')}
                    size="small"
                    color="inherit"
                    aria-label={t('notificationSettingsRegionAdd.clearSearch')}
                  >
                    <HighlightOffIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
            htmlInput: {
              'aria-label': t('notificationSettingsRegionAdd.search'),
            },
            inputLabel: { shrink: false },
          }}
        />
      </Box>

      <Box
        className={classes.lists}
        onScroll={handleScroll}
        ref={listsBoxRef}
        aria-label={t('notificationSettingsRegionAdd.countiesLists')}
      >
        {hasNoResults ? (
          <Typography
            color="textSecondary"
            sx={{ textAlign: 'center', padding: 3 }}
          >
            {t('notificationSettingsRegionAdd.noResults')}
          </Typography>
        ) : (
          renderedRegionSections
        )}
      </Box>

      <Box
        sx={{ padding: 2, paddingBottom: 0 }}
        className={
          isScrollable && !scrollPosition.bottom
            ? classes.shadowBottom
            : undefined
        }
      >
        <Button
          fullWidth
          size="large"
          onClick={handleSave}
          disabled={!pendingChanges || isPending}
          sx={{ fontWeight: 'bold' }}
        >
          {isPending ? (
            <CircularProgress color="inherit" size={24} />
          ) : (
            t('common.save')
          )}
        </Button>
      </Box>
    </Box>
  );
};
